import axios, { AxiosError } from 'axios';
import { API_CONFIG } from '@/config/api.config';
import { encryptData, decryptData } from '@/utils/encryption';

// Configure axios defaults
axios.defaults.withCredentials = true;

// Add axios interceptor for global 403 handling
axios.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response && error.response.status === 403) {
      const url = error.config?.url || '';

      // Specific endpoints that return 403 for permission issues (not session timeout)
      const permissionEndpoints = [
        '/admin/',
        '/session/grant_user_access',
        '/session/get_session',
        '/ids/delete',
      ];

      const isPermissionDenial = permissionEndpoints.some(endpoint =>
        url.includes(endpoint)
      );

      if (!isPermissionDenial) {
        console.warn('Session expired. Please refresh the page and login again.');
        window.dispatchEvent(new CustomEvent('sessionExpired', {
          detail: { message: 'Session expired. Please refresh and login again.', error }
        }));
      }
    }

    return Promise.reject(error);
  }
);

// Types
export interface LoginData {
  username: string;
  password: string;
}

export interface SignupData {
  username: string;
  email: string;
  password: string;
}

export interface LoginResponse {
  id_token: string;
  username: string;
  email: string;
  account_type?: string;
  access_token?: string;
  refresh_token?: string;
}

export interface ApiError {
  message: string;
  error?: string;
  status?: number;
}

export interface OAuthExchangePayload {
  code: string;
  redirectUri: string;
  provider?: string;
}

const persistAuthSession = (payload: LoginResponse) => {
  if (!payload) return;

  if (payload.id_token) {
    localStorage.setItem('id_token', payload.id_token);
  }
  if (payload.username) {
    localStorage.setItem('username', payload.username);
  }
  if (payload.email) {
    localStorage.setItem('email', payload.email);
  }
  if (payload.account_type) {
    localStorage.setItem('account_type', payload.account_type);
  }
};

const buildApiError = (axiosError: AxiosError<string>, fallbackMessage: string): ApiError => {
  let errorData: any = axiosError.response?.data;
  try {
    if (typeof errorData === 'string' && errorData.length > 0) {
      errorData = decryptData(errorData);
    }
  } catch {
    // Ignore decryption issues and fall back to original payload
  }

  return {
    message: errorData?.message || fallbackMessage,
    error: errorData?.error || errorData,
    status: axiosError.response?.status,
  };
};

// Authentication API functions
export const authApi = {
  // User login
  login: async (loginData: LoginData): Promise<LoginResponse> => {
    console.log('=== LOGIN API CALL START ===');
    console.log('API Base URL:', API_CONFIG.baseUrl);
    console.log('Login endpoint:', `${API_CONFIG.baseUrl}${API_CONFIG.endpoints.auth.login}`);
    console.log('Login data (not encrypted):', { username: loginData.username, password: '***' });

    try {
      console.log('Encrypting login data...');
      const encryptedData = encryptData(loginData);
      console.log('Encrypted data length:', encryptedData.length);

      console.log('Making POST request to:', `${API_CONFIG.baseUrl}${API_CONFIG.endpoints.auth.login}`);
      const response = await axios.post(
        `${API_CONFIG.baseUrl}${API_CONFIG.endpoints.auth.login}`,
        { data: encryptedData }
      );

      console.log('Response status:', response.status);
      console.log('Response received, decrypting...');
      const decryptedResponse = decryptData<LoginResponse>(response.data);
      console.log('Decrypted response:', { ...decryptedResponse, id_token: decryptedResponse.id_token ? '***' : undefined });

      persistAuthSession(decryptedResponse);
      console.log('Session tokens stored in localStorage');

      console.log('=== LOGIN API CALL SUCCESS ===');
      return decryptedResponse;
    } catch (error) {
      console.error('=== LOGIN API CALL FAILED ===');
      console.error('Error details:', error);

      const axiosError = error as AxiosError<string>;
      const apiError = buildApiError(axiosError, 'Login failed');
      console.error('API Error object:', apiError);
      throw apiError;
    }
  },

  // User signup
  signup: async (signupData: SignupData): Promise<{ message: string }> => {
    const encryptedData = encryptData(signupData);
    const response = await axios.post(
      `${API_CONFIG.baseUrl}${API_CONFIG.endpoints.auth.signup}`,
      { data: encryptedData }
    );
    const decryptedResponse = decryptData<{ message?: string; error?: string }>(response.data);

    // Check if the response contains an error (backend returns 200 with error field)
    if (decryptedResponse.error) {
      throw {
        message: decryptedResponse.error,
        error: decryptedResponse.error,
        status: response.status,
      } as ApiError;
    }

    return decryptedResponse as { message: string };
  },

  // Confirm signup with verification code
  confirmSignup: async (username: string, code: string, consentIp: string, consentTime: string): Promise<{ message: string }> => {
    try {
      const encryptedData = encryptData({
        username,
        confirmation_code: code,
        consent_ip: consentIp,
        consent_time: consentTime
      });
      const response = await axios.post(
        `${API_CONFIG.baseUrl}${API_CONFIG.endpoints.auth.confirmSignup}`,
        { data: encryptedData }
      );
      return decryptData<{ message: string }>(response.data);
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      throw {
        message: axiosError.response?.data?.message || 'Confirmation failed',
        error: axiosError.response?.data,
        status: axiosError.response?.status,
      } as ApiError;
    }
  },

  // Resend confirmation code
  resendCode: async (username: string): Promise<{ message: string }> => {
    try {
      const encryptedData = encryptData({ username });
      const response = await axios.post(
        `${API_CONFIG.baseUrl}${API_CONFIG.endpoints.auth.resendCode}`,
        { data: encryptedData }
      );
      return decryptData<{ message: string }>(response.data);
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      throw {
        message: axiosError.response?.data?.message || 'Failed to resend code',
        error: axiosError.response?.data,
        status: axiosError.response?.status,
      } as ApiError;
    }
  },

  // Forgot password - initiate reset
  forgotPassword: async (username: string): Promise<{ message: string }> => {
    try {
      const encryptedData = encryptData({ username });
      const response = await axios.post(
        `${API_CONFIG.baseUrl}${API_CONFIG.endpoints.auth.forgotPassword}`,
        { data: encryptedData }
      );
      return decryptData<{ message: string }>(response.data);
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      throw {
        message: axiosError.response?.data?.message || 'Failed to initiate password reset',
        error: axiosError.response?.data,
        status: axiosError.response?.status,
      } as ApiError;
    }
  },

  // Confirm new password with verification code
  confirmNewPassword: async (
    username: string,
    code: string,
    newPassword: string
  ): Promise<{ message: string }> => {
    try {
      const encryptedData = encryptData({ username, code, newPassword });
      const response = await axios.post(
        `${API_CONFIG.baseUrl}${API_CONFIG.endpoints.auth.confirmNewPassword}`,
        { data: encryptedData }
      );
      return decryptData<{ message: string }>(response.data);
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      throw {
        message: axiosError.response?.data?.message || 'Failed to reset password',
        error: axiosError.response?.data,
        status: axiosError.response?.status,
      } as ApiError;
    }
  },

  exchangeOAuthCode: async ({
    code,
    redirectUri,
    provider = 'Google',
  }: OAuthExchangePayload): Promise<LoginResponse> => {
    try {
      const encryptedData = encryptData({ code, redirect_uri: redirectUri, provider });
      const response = await axios.post(
        `${API_CONFIG.baseUrl}${API_CONFIG.endpoints.auth.oauthExchange}`,
        { data: encryptedData }
      );

      const decryptedResponse = decryptData<LoginResponse>(response.data);
      persistAuthSession(decryptedResponse);
      return decryptedResponse;
    } catch (error) {
      const axiosError = error as AxiosError<string>;
      throw buildApiError(axiosError, 'OAuth sign-in failed');
    }
  },

  // Logout
  logout: async (): Promise<void> => {
    try {
      await axios.post(`${API_CONFIG.baseUrl}${API_CONFIG.endpoints.auth.logout}`);
      localStorage.removeItem('id_token');
      localStorage.removeItem('username');
    } catch (error) {
      // Clear local storage even if API call fails
      localStorage.removeItem('id_token');
      localStorage.removeItem('username');
      throw error;
    }
  },

  // Check if user is logged in and refresh token
  checkLogin: async (): Promise<any> => {
    try {
      const idToken = localStorage.getItem('id_token');
      const username = localStorage.getItem('username');
      const email = localStorage.getItem('email');

      if (!idToken || !username) {
        return null;
      }

      const encryptedData = encryptData({ id_token: idToken, username: username });
      const response = await axios.post(
        `${API_CONFIG.baseUrl}${API_CONFIG.endpoints.auth.checkLogin}`,
        { credentials: "include", data: encryptedData }
      );

      const decryptedResponse = decryptData<any>(response.data);

      // Update localStorage with new id_token if provided
      if (decryptedResponse?.id_token) {
        localStorage.setItem('id_token', decryptedResponse.id_token);
      }

      // Update email if provided in response
      if (decryptedResponse?.email) {
        localStorage.setItem('email', decryptedResponse.email);
      }

      // Update account_type if provided in response
      if (decryptedResponse?.account_type) {
        localStorage.setItem('account_type', decryptedResponse.account_type);
      }

      // Add email from localStorage if not in response
      if (!decryptedResponse?.email && email) {
        decryptedResponse.email = email;
      }

      return decryptedResponse;
    } catch (error) {
      console.error('Check login error:', error);
      return null;
    }
  },
};

// Session API functions
export interface Session {
  session_id: string;
  session_name: string;
  username: string;
  created: string;
  last_modified: string;
  folder_id?: string;
  folder_name?: string;
  permissions?: string;
  session_context?: {
    last_context_type?: string;
    last_context_number?: string;
    resource_ids?: string[];
    resource_names?: string[];
    current_patents?: string[];
    current_applications?: string[];
    current_publications?: string[];
    pros_docs?: string[];
  };
  chat_history?: Array<{
    role: string;
    content: string;
  }>;
  user_access?: Array<{
    username: string;
    permissions: string;
  }>;
}

export const sessionApi = {
  // Fetch user sessions
  fetchSessions: async (): Promise<Session[]> => {
    try {
      const idToken = localStorage.getItem('id_token');
      if (!idToken) {
        throw new Error('No ID token found');
      }

      const encryptedData = encryptData({ id_token: idToken });
      const response = await axios.post(
        `${API_CONFIG.baseUrl}/session/sessions`,
        { data: encryptedData }
      );

      const decryptedData = decryptData<any>(response.data);

      // The response might have a 'sessions' or 'chats' key
      const sessions = decryptedData.sessions || decryptedData.chats || decryptedData;

      return Array.isArray(sessions) ? sessions : [];
    } catch (error) {
      console.error('Failed to fetch sessions:', error);
      return [];
    }
  },

  // Fetch session with full history
  fetchSessionWithHistory: async (sessionId: string): Promise<Session | null> => {
    try {
      const idToken = localStorage.getItem('id_token');
      if (!idToken) {
        throw new Error('No ID token found');
      }

      const encryptedData = encryptData({
        id_token: idToken,
        session_id: sessionId
      });

      const response = await axios.post(
        `${API_CONFIG.baseUrl}/session/get_session`,
        { data: encryptedData }
      );

      const decryptedData = decryptData<any>(response.data);
      const session = decryptedData?.session || decryptedData || null;
      console.log('[API.fetchSessionWithHistory] Received session_context.current_drafts:', session?.session_context?.current_drafts);
      return session;
    } catch (error) {
      console.error('Failed to fetch session with history:', error);
      return null;
    }
  },

  // Add a new session (save for first time)
  addSession: async (
    sessionName: string,
    messages: ChatMessage[],
    folderId?: string | null
  ): Promise<{ message: string; session_id?: string }> => {
    try {
      const idToken = localStorage.getItem('id_token');
      if (!idToken) throw new Error('No ID token found');

      // Get session context from localStorage
      const sessionContextStr = localStorage.getItem('session_context');
      const sessionContext = sessionContextStr ? JSON.parse(sessionContextStr) : undefined;

      // Get patents data
      const patentsStr = localStorage.getItem('patents');
      const patents = patentsStr ? JSON.parse(patentsStr) : undefined;

      const requestData = {
        session_name: sessionName,
        messages,
        folder_id: folderId,
        session_context: sessionContext,
        patents,
        id_token: idToken,
      };

      console.log('[API.addSession] Request data session_context.current_drafts:', sessionContext?.current_drafts);

      const encryptedData = encryptData(requestData);

      const response = await axios.post(
        `${API_CONFIG.baseUrl}${API_CONFIG.endpoints.session.addSession}`,
        { data: encryptedData },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      return decryptData<{ message: string; session_id?: string }>(response.data);
    } catch (error) {
      console.error('Failed to add session:', error);
      throw error;
    }
  },

  // Save an existing session (update)
  saveSession: async (
    sessionId: string,
    sessionName: string,
    messages: ChatMessage[],
    folderId?: string | null
  ): Promise<{ message: string; session_id?: string }> => {
    try {
      const idToken = localStorage.getItem('id_token');
      if (!idToken) throw new Error('No ID token found');

      // Get session context from localStorage
      const sessionContextStr = localStorage.getItem('session_context');
      const sessionContext = sessionContextStr ? JSON.parse(sessionContextStr) : undefined;

      // Get patents data
      const patentsStr = localStorage.getItem('patents');
      const patents = patentsStr ? JSON.parse(patentsStr) : undefined;

      const requestData = {
        session_id: sessionId,
        session_name: sessionName,
        folder_id: folderId,
        messages,
        session_context: sessionContext,
        patents,
        id_token: idToken,
      };

      console.log('[API.saveSession] Request data session_context.current_drafts:', sessionContext?.current_drafts);

      const encryptedData = encryptData(requestData);

      const response = await axios.post(
        `${API_CONFIG.baseUrl}${API_CONFIG.endpoints.session.saveSession}`,
        { data: encryptedData },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      return decryptData<{ message: string; session_id?: string }>(response.data);
    } catch (error) {
      console.error('Failed to save session:', error);
      throw error;
    }
  },

  // Fetch user folders
  fetchFolders: async (): Promise<Array<{ folder_id: string; folder_name: string }>> => {
    try {
      const idToken = localStorage.getItem('id_token');
      if (!idToken) {
        throw new Error('No ID token found');
      }

      const encryptedData = encryptData({ id_token: idToken });
      const response = await axios.post(
        `${API_CONFIG.baseUrl}/session/folders`,
        { data: encryptedData }
      );

      const decryptedData = decryptData<any>(response.data);
      const folders = decryptedData.folders || decryptedData;

      return Array.isArray(folders) ? folders : [];
    } catch (error) {
      console.error('Failed to fetch folders:', error);
      return [];
    }
  },

  // Create a new folder
  createFolder: async (folderName: string): Promise<{ folder_id: string; folder_name: string }> => {
    try {
      const idToken = localStorage.getItem('id_token');
      if (!idToken) {
        throw new Error('No ID token found');
      }

      const encryptedData = encryptData({ id_token: idToken, folder_name: folderName });
      const response = await axios.post(
        `${API_CONFIG.baseUrl}/session/create_folder`,
        { data: encryptedData }
      );

      return decryptData(response.data);
    } catch (error) {
      console.error('Failed to create folder:', folderName, error);
      throw error;
    }
  },

  // Delete a session
  deleteSession: async (sessionId: string): Promise<any> => {
    try {
      const idToken = localStorage.getItem('id_token');
      if (!idToken) {
        throw new Error('No ID token found');
      }

      const encryptedData = encryptData({ id_token: idToken, session_id: sessionId });
      const response = await axios.post(
        `${API_CONFIG.baseUrl}/session/delete_session`,
        { data: encryptedData }
      );

      return decryptData(response.data);
    } catch (error) {
      console.error('Failed to delete session:', sessionId, error);
      throw error;
    }
  },

  // Delete a folder
  deleteFolder: async (folderId: string): Promise<any> => {
    try {
      const idToken = localStorage.getItem('id_token');
      if (!idToken) {
        throw new Error('No ID token found');
      }

      const encryptedData = encryptData({ id_token: idToken, folder_id: folderId });
      const response = await axios.post(
        `${API_CONFIG.baseUrl}/session/delete_folder`,
        { data: encryptedData }
      );

      return decryptData(response.data);
    } catch (error) {
      console.error('Failed to delete folder:', folderId, error);
      throw error;
    }
  },

  moveSessionToFolder: async (sessionId: string, oldFolderId: string | null, newFolderId: string | null): Promise<any> => {
    try {
      const idToken = localStorage.getItem('id_token');
      if (!idToken) {
        throw new Error('No ID token found');
      }

      const encryptedData = encryptData({
        id_token: idToken,
        session_id: sessionId,
        old_folder_id: oldFolderId,
        new_folder_id: newFolderId
      });

      const response = await axios.post(
        `${API_CONFIG.baseUrl}/session/move_session_to_folder`,
        { data: encryptedData }
      );

      return decryptData(response.data);
    } catch (error) {
      console.error('Failed to move session to folder:', error);
      throw error;
    }
  },

  renameSession: async (sessionId: string, newSessionName: string): Promise<any> => {
    try {
      const idToken = localStorage.getItem('id_token');
      if (!idToken) {
        throw new Error('No ID token found');
      }

      const encryptedData = encryptData({
        id_token: idToken,
        session_id: sessionId,
        new_session_name: newSessionName
      });

      const response = await axios.post(
        `${API_CONFIG.baseUrl}/session/rename_session`,
        { data: encryptedData }
      );

      return decryptData(response.data);
    } catch (error) {
      console.error('Failed to rename session:', error);
      throw error;
    }
  },

  renameFolder: async (folderId: string, newFolderName: string): Promise<any> => {
    try {
      const idToken = localStorage.getItem('id_token');
      if (!idToken) {
        throw new Error('No ID token found');
      }

      const encryptedData = encryptData({
        id_token: idToken,
        folder_id: folderId,
        new_folder_name: newFolderName
      });

      const response = await axios.post(
        `${API_CONFIG.baseUrl}/session/rename_folder`,
        { data: encryptedData }
      );

      return decryptData(response.data);
    } catch (error) {
      console.error('Failed to rename folder:', error);
      throw error;
    }
  },
};

// Document API functions
export interface SessionDocument {
  resource_id: string;
  file_name: string;
  file_type: string; // 'upload', 'patent', 'publication', 'application', 'claims', 'oa', 'foreign_patent'
  title?: string; // Document title (for patents/publications), otherwise uses file_name
  upload_date?: string;
  last_accessed_date?: string;
  create_date?: string;
  file_size?: number;
  summary?: string;
  metadata?: any;
}

export interface DocumentContent {
  title?: string;
  abstract?: string;
  description?: string | { // Can be HTML string, structured sections, or flat dict
    sections?: Array<{
      title: string;
      paragraphs: Array<{
        number: string | number;
        content: string;
        text?: string;
      }>;
    }>;
  } | Record<string, string>;
  claims?: string | Array<{ // Can be HTML string or array
    claim_number: string;
    text: string;
    dependencies?: string[];
    claim_refs?: string[];
  }> | Record<string, any>; // Array for patents, dict for foreign patents
  claim_support_map?: Record<string, string[]>;
  number_claims?: string | number;
  summary?: string | object;
  content?: string; // For uploads
  drawing_urls?: string[];
  drawing_summaries?: string[];
  drawings?: string[]; // Array of drawing URLs
  spec_map?: any;
  parts_list?: any;
  status?: string;
  application_number?: string; // Application number for patents/publications
}

export const documentApi = {
  // Fetch session documents
  fetchSessionDocuments: async (limit?: number): Promise<SessionDocument[]> => {
    try {
      const idToken = localStorage.getItem('id_token');
      if (!idToken) {
        throw new Error('No ID token found');
      }

      const payload: any = { id_token: idToken };
      if (limit) {
        payload.limit = limit;
      }

      const encryptedData = encryptData(payload);
      const response = await axios.post(
        `${API_CONFIG.baseUrl}/session/documents`,
        { data: encryptedData }
      );

      const decryptedData = decryptData<any>(response.data);

      // The response might have a 'session_documents' key or be the array directly
      const documents = decryptedData.session_documents || decryptedData;

      return Array.isArray(documents) ? documents : [];
    } catch (error) {
      console.error('Failed to fetch session documents:', error);
      return [];
    }
  },

  // Delete document
  deleteDocument: async (fileType: string, resourceId: string): Promise<void> => {
    console.log('[API] deleteDocument called with:', { fileType, resourceId });

    try {
      const idToken = localStorage.getItem('id_token');
      if (!idToken) {
        throw new Error('No ID token found');
      }

      if (!fileType || !resourceId) {
        throw new Error(`Missing required parameters: fileType=${fileType}, resourceId=${resourceId}`);
      }

      console.log('[API] Encrypting data for delete request');
      const encryptedData = encryptData({
        id_token: idToken,
        file_type: fileType,
        resource_id: resourceId
      });

      console.log('[API] Sending POST request to:', `${API_CONFIG.baseUrl}/doc/delete`);
      const response = await axios.post(
        `${API_CONFIG.baseUrl}/doc/delete`,
        { data: encryptedData }
      );
      console.log('[API] Delete request successful:', response.status);
    } catch (error) {
      console.error('[API] Failed to delete document:', error);
      if (axios.isAxiosError(error)) {
        console.error('[API] Axios error details:', {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status
        });
      }
      throw error;
    }
  },

  // Fetch document content
  fetchDocument: async (docType: string, docId: string): Promise<DocumentContent | null> => {
    try {
      const idToken = localStorage.getItem('id_token');
      if (!idToken) {
        throw new Error('No ID token found');
      }

      const encryptedData = encryptData({
        id_token: idToken,
        doc_type: docType,
        doc_id: docId
      });

      const response = await axios.post(
        `${API_CONFIG.baseUrl}/doc/doc`,
        { data: encryptedData }
      );

      const decryptedData = decryptData<DocumentContent>(response.data);
      return decryptedData;
    } catch (error) {
      console.error('Failed to fetch document:', error);
      return null;
    }
  },

  // Fetch drawings info for a document
  fetchDrawingsInfo: async (docType: string, docId: string): Promise<{ drawings_info?: any[]; urls?: string[] } | null> => {
    try {
      const idToken = localStorage.getItem('id_token');
      if (!idToken) {
        throw new Error('No ID token found');
      }

      const encryptedData = encryptData({
        id_token: idToken,
        doc_type: docType,
        doc_id: docId
      });

      const response = await axios.post(
        `${API_CONFIG.baseUrl}/doc/drawings_info`,
        { data: encryptedData }
      );

      const decryptedData = decryptData<any>(response.data);
      return decryptedData;
    } catch (error) {
      console.error('Failed to fetch drawings info:', error);
      return null;
    }
  },

  // Fetch and load document (async load on backend)
  fetchAndLoadDocument: async (docType: string, docId: string): Promise<{ job_id?: string } | null> => {
    try {
      const idToken = localStorage.getItem('id_token');
      if (!idToken) {
        throw new Error('No ID token found');
      }

      const encryptedData = encryptData({
        id_token: idToken,
        doc_type: docType,
        doc_id: docId
      });

      const response = await axios.post(
        `${API_CONFIG.baseUrl}/doc/fetch_and_load_doc`,
        { data: encryptedData }
      );

      const decryptedData = decryptData<any>(response.data);
      return decryptedData;
    } catch (error) {
      console.error('Failed to load document:', error);
      return null;
    }
  },

  // Get job status for async document loading
  getJobStatus: async (jobId: string): Promise<{ status?: string } | null> => {
    try {
      const idToken = localStorage.getItem('id_token');
      if (!idToken) {
        throw new Error('No ID token found');
      }

      const encryptedData = encryptData({
        id_token: idToken,
        query: jobId
      });

      const response = await axios.post(
        `${API_CONFIG.baseUrl}/task/job_status`,
        { data: encryptedData }
      );

      const decryptedData = decryptData<any>(response.data);
      return decryptedData;
    } catch (error) {
      console.error('Failed to get job status:', error);
      return null;
    }
  },

  // Semantic search documents (full text search)
  semanticSearchDocuments: async (queryText: string, topK: number = 20): Promise<any> => {
    try {
      const idToken = localStorage.getItem('id_token');
      if (!idToken) {
        throw new Error('No ID token found');
      }

      // Get session documents from sessionStorage if available
      const cachedSessionDocs = sessionStorage.getItem('session_documents');
      const sessionDocuments = cachedSessionDocs ? JSON.parse(cachedSessionDocs) : [];

      const encryptedData = encryptData({
        id_token: idToken,
        query_text: queryText,
        top_k: topK,
        session_documents: sessionDocuments
      });

      const response = await axios.post(
        `${API_CONFIG.baseUrl}/doc/semantic_search`,
        { data: encryptedData }
      );

      return decryptData<any>(response.data);
    } catch (error) {
      console.error('Failed to perform semantic search:', error);
      throw error;
    }
  },

  // Submit a custom question about a document
  customQuestion: async (text: string, docType: string, docId: string): Promise<{message_id?: string; error?: string}> => {
    try {
      const idToken = localStorage.getItem('id_token');
      if (!idToken) throw new Error('No ID token found');

      // Get response detail level from user settings
      const userStorageStr = localStorage.getItem('user-storage');
      const userStorage = userStorageStr ? JSON.parse(userStorageStr) : {};
      const userSettings = userStorage.state?.userSettings || {};
      const responseDetailLevel = userSettings.responseDetailLevel || 'medium';
      console.log('[DOCUMENT API] Response detail level to send:', responseDetailLevel);

      const encryptedData = encryptData({
        id_token: idToken,
        text,
        doc_id: docId,
        doc_type: docType,
        response_detail_level: responseDetailLevel,
      });

      const response = await axios.post(
        `${API_CONFIG.baseUrl}/doc/custom_question`,
        { data: encryptedData }
      );

      return decryptData<{message_id?: string}>(response.data);
    } catch (error) {
      console.error('Failed to submit custom question:', error);
      return { error: 'Failed to submit question' };
    }
  },

  // Get custom question history for a document
  getCustomQuestionHistory: async (docId: string, docType: string): Promise<Array<{
    input_text: string;
    response: string | {output: string};
    timestamp: string;
  }>> => {
    try {
      const idToken = localStorage.getItem('id_token');
      if (!idToken) throw new Error('No ID token found');

      const encryptedData = encryptData({
        id_token: idToken,
        doc_id: docId,
        doc_type: docType,
      });

      const response = await axios.post(
        `${API_CONFIG.baseUrl}/doc/custom_question_history`,
        { data: encryptedData }
      );

      const decryptedResponse = decryptData<any>(response.data);

      if (Array.isArray(decryptedResponse)) {
        return decryptedResponse;
      }

      return [];
    } catch (error) {
      console.error('Failed to get custom question history:', error);
      return [];
    }
  },

  // Get document comments for a user
  getDocumentComments: async (docType: string, docId: string): Promise<DocumentComment[]> => {
    try {
      const idToken = localStorage.getItem('id_token');
      if (!idToken) throw new Error('No ID token found');

      const resourceId = `${docType}-${docId}`;
      console.log('[API] Getting comments for:', { docType, docId, resourceId });
      const encryptedData = encryptData({
        id_token: idToken,
        resource_id: resourceId,
      });

      const response = await axios.post(
        `${API_CONFIG.baseUrl}/doc/comments/get`,
        { data: encryptedData }
      );

      const decryptedResponse = decryptData<{ comments: DocumentComment[] }>(response.data);
      console.log('[API] Got comments:', decryptedResponse?.comments);
      return decryptedResponse?.comments || [];
    } catch (error) {
      console.error('Failed to get document comments:', error);
      return [];
    }
  },

  // Save document comments for a user
  saveDocumentComments: async (docType: string, docId: string, comments: DocumentComment[]): Promise<boolean> => {
    try {
      const idToken = localStorage.getItem('id_token');
      if (!idToken) throw new Error('No ID token found');

      const resourceId = `${docType}-${docId}`;
      console.log('[API] Saving comments for:', { docType, docId, resourceId, numComments: comments.length });
      const encryptedData = encryptData({
        id_token: idToken,
        resource_id: resourceId,
        comments: comments,
      });

      await axios.post(
        `${API_CONFIG.baseUrl}/doc/comments/save`,
        { data: encryptedData }
      );

      console.log('[API] Comments saved successfully');
      return true;
    } catch (error) {
      console.error('Failed to save document comments:', error);
      return false;
    }
  },

  // Delete a specific comment
  deleteDocumentComment: async (docType: string, docId: string, commentId: string): Promise<boolean> => {
    try {
      const idToken = localStorage.getItem('id_token');
      if (!idToken) throw new Error('No ID token found');

      const resourceId = `${docType}-${docId}`;
      const encryptedData = encryptData({
        id_token: idToken,
        resource_id: resourceId,
        comment_id: commentId,
      });

      await axios.post(
        `${API_CONFIG.baseUrl}/doc/comments/delete`,
        { data: encryptedData }
      );

      return true;
    } catch (error) {
      console.error('Failed to delete document comment:', error);
      return false;
    }
  },

  // Search documents
  searchDocuments: async (
    query: string,
    limit: number = 50,
    docTypes?: string[]
  ): Promise<SessionDocument[]> => {
    try {
      console.log('[API] searchDocuments called with query:', query, 'limit:', limit);
      const idToken = localStorage.getItem('id_token');
      if (!idToken) {
        throw new Error('No ID token found');
      }

      const encryptedData = encryptData({
        id_token: idToken,
        query,
        limit,
        doc_types: docTypes,
      });

      console.log('[API] Sending request to:', `${API_CONFIG.baseUrl}/doc/search_documents`);
      const response = await axios.post(
        `${API_CONFIG.baseUrl}/doc/search_documents`,
        { data: encryptedData }
      );

      console.log('[API] Raw response:', response.data);
      const decryptedResponse = decryptData<{ documents: SessionDocument[]; total: number; query: string }>(response.data);
      console.log('[API] Decrypted response:', decryptedResponse);
      return decryptedResponse.documents || [];
    } catch (error) {
      console.error('[API] Failed to search documents:', error);
      return [];
    }
  },
};

// Document comment type
export interface DocumentComment {
  id: string;
  paragraph_index: number;
  selected_text: string;
  comment_text: string;
  section: string;
  text_offset?: number; // Offset from start of paragraph/section for precise matching
  created_at: string;
  updated_at: string;
}

// Resource API functions
export const resourceApi = {
  // Fetch fee information
  fetchFeeInfo: async (): Promise<any> => {
    const cacheKey = 'fee_info';
    const cachedData = localStorage.getItem(cacheKey);

    if (cachedData) {
      try {
        return JSON.parse(cachedData);
      } catch (e) {
        // Continue to fetch if cache is invalid
      }
    }

    try {
      const idToken = localStorage.getItem('id_token');
      if (!idToken) throw new Error('No ID token found');

      const encryptedData = encryptData({ id_token: idToken });
      const response = await axios.post(
        `${API_CONFIG.baseUrl}/resource/fee_info`,
        { data: encryptedData }
      );

      const data = decryptData<any>(response.data);
      localStorage.setItem(cacheKey, JSON.stringify(data));
      return data;
    } catch (error) {
      console.error('Failed to fetch fee info:', error);
      return null;
    }
  },

  // Fetch USC information
  fetchUscInfo: async (): Promise<any> => {
    const cacheKey = 'usc_info';
    const cachedData = localStorage.getItem(cacheKey);

    if (cachedData) {
      try {
        return JSON.parse(cachedData);
      } catch (e) {
        // Continue to fetch if cache is invalid
      }
    }

    try {
      const idToken = localStorage.getItem('id_token');
      if (!idToken) throw new Error('No ID token found');

      const encryptedData = encryptData({ id_token: idToken });
      const response = await axios.post(
        `${API_CONFIG.baseUrl}/resource/usc_info`,
        { data: encryptedData }
      );

      const data = decryptData<any>(response.data);
      localStorage.setItem(cacheKey, JSON.stringify(data));
      return data;
    } catch (error) {
      console.error('Failed to fetch USC info:', error);
      return null;
    }
  },

  // Fetch Federal Register information
  fetchFedRegInfo: async (): Promise<any> => {
    try {
      const idToken = localStorage.getItem('id_token');
      if (!idToken) throw new Error('No ID token found');

      const encryptedData = encryptData({ id_token: idToken });
      const response = await axios.post(
        `${API_CONFIG.baseUrl}/resource/fed_reg_info`,
        { data: encryptedData }
      );

      const data = decryptData<any>(response.data);
      return data;
    } catch (error) {
      console.error('Failed to fetch Federal Register info:', error);
      return null;
    }
  },

  // Fetch MPEP structure/chapters
  fetchMpepInfo: async (): Promise<any> => {
    const cacheKey = 'mpep_structure';
    const cachedData = localStorage.getItem(cacheKey);

    if (cachedData) {
      try {
        return JSON.parse(cachedData);
      } catch (e) {
        // Continue to fetch if cache is invalid
      }
    }

    try {
      const idToken = localStorage.getItem('id_token');
      if (!idToken) throw new Error('No ID token found');

      const encryptedData = encryptData({ id_token: idToken });
      const response = await axios.post(
        `${API_CONFIG.baseUrl}/resource/mpep_info`,
        { data: encryptedData }
      );

      const data = decryptData<any>(response.data);
      if (data && data.chapters) {
        localStorage.setItem(cacheKey, JSON.stringify(data.chapters));
        return data.chapters;
      }
      return data;
    } catch (error) {
      console.error('Failed to fetch MPEP structure:', error);
      return null;
    }
  },

  // Fetch specific MPEP section content
  fetchMpepContent: async (section: string): Promise<any> => {
    try {
      const idToken = localStorage.getItem('id_token');
      if (!idToken) throw new Error('No ID token found');

      const encryptedData = encryptData({
        id_token: idToken,
        section: section
      });

      const response = await axios.post(
        `${API_CONFIG.baseUrl}/resource/mpep`,
        { data: encryptedData }
      );

      return decryptData<any>(response.data);
    } catch (error) {
      console.error('Failed to fetch MPEP content:', error);
      return null;
    }
  },

  // Fetch specific CFR section content
  fetchCfrContent: async (section: string): Promise<any> => {
    try {
      const idToken = localStorage.getItem('id_token');
      if (!idToken) throw new Error('No ID token found');

      const encryptedData = encryptData({
        id_token: idToken,
        doc_type: 'cfr',
        doc_id: section
      });

      const response = await axios.post(
        `${API_CONFIG.baseUrl}/doc/doc`,
        { data: encryptedData }
      );

      return decryptData<any>(response.data);
    } catch (error) {
      console.error('Failed to fetch CFR content:', error);
      return null;
    }
  },

  // Fetch specific USC section content
  fetchUscContent: async (section: string): Promise<any> => {
    try {
      const idToken = localStorage.getItem('id_token');
      if (!idToken) throw new Error('No ID token found');

      const encryptedData = encryptData({
        id_token: idToken,
        doc_type: 'usc',
        doc_id: section
      });

      const response = await axios.post(
        `${API_CONFIG.baseUrl}/doc/doc`,
        { data: encryptedData }
      );

      return decryptData<any>(response.data);
    } catch (error) {
      console.error('Failed to fetch USC content:', error);
      return null;
    }
  },

  // Perform MPEP query
  performMpepQuery: async (query: string): Promise<any> => {
    try {
      const idToken = localStorage.getItem('id_token');
      if (!idToken) throw new Error('No ID token found');

      const encryptedData = encryptData({
        id_token: idToken,
        mpep_query: query
      });

      const response = await axios.post(
        `${API_CONFIG.baseUrl}/resource/query`,
        { data: encryptedData }
      );

      return decryptData<any>(response.data);
    } catch (error) {
      console.error('Failed to perform MPEP query:', error);
      return null;
    }
  },

  // Perform CFR query
  performCfrQuery: async (query: string): Promise<any> => {
    try {
      const idToken = localStorage.getItem('id_token');
      if (!idToken) throw new Error('No ID token found');

      const encryptedData = encryptData({
        id_token: idToken,
        cfr_query: query
      });

      const response = await axios.post(
        `${API_CONFIG.baseUrl}/resource/query`,
        { data: encryptedData }
      );

      return decryptData<any>(response.data);
    } catch (error) {
      console.error('Failed to perform CFR query:', error);
      return null;
    }
  },

  // Perform Federal Register query
  performFedRegQuery: async (query: string): Promise<any> => {
    try {
      const idToken = localStorage.getItem('id_token');
      if (!idToken) throw new Error('No ID token found');

      const encryptedData = encryptData({
        id_token: idToken,
        fed_reg_query: query
      });

      const response = await axios.post(
        `${API_CONFIG.baseUrl}/resource/query`,
        { data: encryptedData }
      );

      return decryptData<any>(response.data);
    } catch (error) {
      console.error('Failed to perform Federal Register query:', error);
      return null;
    }
  },

  // Perform USC query
  performUscQuery: async (query: string): Promise<any> => {
    try {
      const idToken = localStorage.getItem('id_token');
      if (!idToken) throw new Error('No ID token found');

      const encryptedData = encryptData({
        id_token: idToken,
        usc_query: query
      });

      const response = await axios.post(
        `${API_CONFIG.baseUrl}/resource/query`,
        { data: encryptedData }
      );

      return decryptData<any>(response.data);
    } catch (error) {
      console.error('Failed to perform USC query:', error);
      return null;
    }
  },

  // Fetch Federal Register content from S3
  fetchFedRegContent: async (fedRegId: string): Promise<any> => {
    try {
      const idToken = localStorage.getItem('id_token');
      if (!idToken) throw new Error('No ID token found');

      const encryptedData = encryptData({
        id_token: idToken,
        fed_reg_id: fedRegId
      });

      const response = await axios.post(
        `${API_CONFIG.baseUrl}/resource/fed_reg_content`,
        { data: encryptedData }
      );

      return decryptData<any>(response.data);
    } catch (error) {
      console.error('Failed to fetch Federal Register content:', error);
      return null;
    }
  },
};

// Chat API functions
export interface ChatMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
  sendTime?: number;
  receiveTime?: number;
  userMessageId?: string;
  message_context?: any;
  session_context?: any;
}

export interface SendMessageRequest {
  message: string;
  history: ChatMessage[];
  userInfo?: any;
  id_token: string;
  session_context?: any;
  session_id?: string;
  new_resource_ids?: string | null;
  new_resource_names?: string | null;
  new_pros_docs?: string | null;
  new_pros_doc_names?: string | null;
  chat_language?: string;
  response_detail_level?: string;
  active_drafts?: any[];
}

export interface SendMessageResponse {
  status?: string;
  message_id?: string;
  message?: string;
  error?: string;
  session_context?: any;
  message_context?: any;
  search_results?: any;
  new_resources?: any[];
  history?: ChatMessage[];
  token_usage?: any;
}

export interface GetMessageResponse {
  message?: string;
  message_context?: any;
  session_context?: any;
  search_results?: any;
  new_resources?: any[];
}

export const chatApi = {
  // Send a chat message
  sendMessage: async (
    message: string,
    history: ChatMessage[],
    userInfo?: any,
    sessionId?: string
  ): Promise<SendMessageResponse> => {
    try {
      const idToken = localStorage.getItem('id_token');
      if (!idToken) throw new Error('No ID token found');

      // Get session context from localStorage
      const sessionContextStr = localStorage.getItem('session_context');
      const sessionContext = sessionContextStr ? JSON.parse(sessionContextStr) : undefined;

      // Get new resource IDs and names
      const newResourceIds = localStorage.getItem('new_resource_ids');
      const newResourceNames = localStorage.getItem('new_resource_names');
      const newProsDocs = localStorage.getItem('new_pros_docs');
      const newProsDocNames = localStorage.getItem('new_pros_doc_names');

      // Get active drafts from localStorage
      const activeDraftsStr = localStorage.getItem('active_drafts');
      const activeDrafts = activeDraftsStr ? JSON.parse(activeDraftsStr) : [];

      // Get chat language and response detail level from user settings
      const userStorageStr = localStorage.getItem('user-storage');
      const userStorage = userStorageStr ? JSON.parse(userStorageStr) : {};
      const userSettings = userStorage.state?.userSettings || {};
      const chatLanguage = userSettings.chatLanguage || 'en';
      const responseDetailLevel = userSettings.responseDetailLevel || 'medium';
      console.log('[CHAT API] User storage:', userStorage);
      console.log('[CHAT API] Chat language to send:', chatLanguage);
      console.log('[CHAT API] Response detail level to send:', responseDetailLevel);

      // Update local history
      localStorage.setItem('history', JSON.stringify([...history, { role: 'user', content: message }]));

      const requestData: SendMessageRequest = {
        message,
        history,
        userInfo,
        id_token: idToken,
        session_context: sessionContext,
        session_id: sessionId,
        new_resource_ids: newResourceIds,
        new_resource_names: newResourceNames,
        new_pros_docs: newProsDocs,
        new_pros_doc_names: newProsDocNames,
        chat_language: chatLanguage,
        response_detail_level: responseDetailLevel,
        active_drafts: activeDrafts,
      };

      const encryptedData = encryptData(requestData);

      const response = await axios.post(
        `${API_CONFIG.baseUrl}${API_CONFIG.endpoints.chat.sendMessage}`,
        { data: encryptedData },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      const decryptedResponse = decryptData<SendMessageResponse>(response.data);

      // Update session context if present
      if (decryptedResponse && decryptedResponse.session_context) {
        localStorage.setItem('session_context', JSON.stringify(decryptedResponse.session_context));
        window.dispatchEvent(new Event('storage-update'));
      }

      // Update history if present
      if (decryptedResponse && decryptedResponse.history) {
        localStorage.setItem('history', JSON.stringify(decryptedResponse.history));
      }

      return decryptedResponse;
    } catch (error) {
      console.error('Failed to send message:', error);
      throw error;
    }
  },

  // Get a message by ID (polling)
  getMessage: async (messageId: string): Promise<GetMessageResponse> => {
    try {
      const idToken = localStorage.getItem('id_token');
      if (!idToken) throw new Error('No ID token found');

      const encryptedData = encryptData({
        message_id: messageId,
        id_token: idToken,
      });

      const response = await axios.post(
        `${API_CONFIG.baseUrl}${API_CONFIG.endpoints.chat.getMessage}`,
        { data: encryptedData },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data && response.data.length > 0) {
        return decryptData<GetMessageResponse>(response.data);
      }

      return {};
    } catch (error) {
      console.error('Failed to get message:', error);
      throw error;
    }
  },

  // Send feedback for a message
  sendFeedback: async (
    feedbackMessage: string,
    isPositive: boolean,
    sessionId?: string
  ): Promise<{ message: string }> => {
    try {
      const idToken = localStorage.getItem('id_token');
      if (!idToken) throw new Error('No ID token found');

      // Get session context from localStorage
      const sessionContextStr = localStorage.getItem('session_context');
      const sessionContext = sessionContextStr ? JSON.parse(sessionContextStr) : undefined;

      // Get session name
      const sessionName = localStorage.getItem('session_name') || undefined;

      // Get history
      const historyStr = localStorage.getItem('history');
      const history = historyStr ? JSON.parse(historyStr) : undefined;

      const requestData = {
        feedback_message: feedbackMessage,
        id_token: idToken,
        is_positive: isPositive,
        session_id: sessionId,
        session_name: sessionName,
        session_context: sessionContext,
        history,
      };

      const encryptedData = encryptData(requestData);

      const response = await axios.post(
        `${API_CONFIG.baseUrl}${API_CONFIG.endpoints.chat.feedback}`,
        { data: encryptedData },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      return decryptData<{ message: string }>(response.data);
    } catch (error) {
      console.error('Failed to send feedback:', error);
      throw error;
    }
  },
};

// Patent/Application API functions
export interface SessionApplication {
  application_id: string;
  application?: {
    applicationMetaData?: any;
    applicationNumberText?: string;
    pgpubDocumentMetaData?: any;
    grantDocumentMetaData?: any;
  };
  title?: string;
  inventor_name?: string;
  assignee_name?: string;
  status?: string;
  filing_date?: string;
  publication_number?: string;
  patent_number?: string;
  last_accessed_date?: string;
  create_date?: string;
}

export const patentApi = {
  // Fetch session applications with caching
  getSessionApplications: async (overrideCache: boolean = false): Promise<SessionApplication[]> => {
    try {
      const cacheKey = 'session_applications';
      const idToken = localStorage.getItem('id_token');

      if (!idToken) {
        throw new Error('No ID token found');
      }

      // Check cache first if not overriding
      if (!overrideCache) {
        const cachedData = localStorage.getItem(cacheKey);
        if (cachedData && cachedData !== 'undefined') {
          try {
            const parsed = JSON.parse(cachedData);
            if (Array.isArray(parsed)) {
              return parsed;
            }
          } catch (e) {
            console.error('Failed to parse cached session applications:', e);
          }
        }
      }

      // Fetch from API
      const encryptedData = encryptData({
        id_token: idToken,
        minimal: true
      });

      const response = await axios.post(
        `${API_CONFIG.baseUrl}/patent/session_applications`,
        { data: encryptedData }
      );

      const decryptedData = decryptData<any>(response.data);
      const applications = decryptedData.session_applications || decryptedData;

      // Cache the result
      if (Array.isArray(applications)) {
        localStorage.setItem(cacheKey, JSON.stringify(applications));
        return applications;
      }

      return [];
    } catch (error) {
      console.error('Failed to fetch session applications:', error);
      return [];
    }
  },

  // Get full application details
  getFullApplication: async (applicationId: string): Promise<any> => {
    try {
      const idToken = localStorage.getItem('id_token');
      if (!idToken) {
        throw new Error('No ID token found');
      }

      const encryptedData = encryptData({
        id_token: idToken,
        application_id: applicationId
      });

      const response = await axios.post(
        `${API_CONFIG.baseUrl}/patent/get_full_application`,
        { data: encryptedData }
      );

      return decryptData(response.data);
    } catch (error) {
      console.error('Failed to fetch full application:', error);
      throw error;
    }
  },

  // Get application number from patent
  getApplicationNumberFromPatent: async (resourceId: string, resourceType: string): Promise<any> => {
    try {
      const idToken = localStorage.getItem('id_token');
      if (!idToken) {
        throw new Error('No ID token found');
      }

      const encryptedData = encryptData({
        id_token: idToken,
        resource_id: resourceId,
        resource_type: resourceType
      });

      const response = await axios.post(
        `${API_CONFIG.baseUrl}/doc/get_app_number`,
        { data: encryptedData }
      );

      return decryptData(response.data);
    } catch (error) {
      console.error('Failed to get application number:', error);
      throw error;
    }
  },

  // Refresh application data from USPTO
  refreshApplication: async (applicationNumber: string): Promise<any> => {
    try {
      const idToken = localStorage.getItem('id_token');
      if (!idToken) {
        throw new Error('No ID token found');
      }

      const encryptedData = encryptData({
        id_token: idToken,
        application_number: applicationNumber
      });

      const response = await axios.post(
        `${API_CONFIG.baseUrl}/patent/refresh_application`,
        { data: encryptedData }
      );

      return decryptData(response.data);
    } catch (error) {
      console.error('Failed to refresh application:', error);
      throw error;
    }
  },

  // Fetch prosecution document info with caching
  fetchProsecutionDocInfo: async (applicationNum: string, overrideCache: boolean = false): Promise<any> => {
    try {
      const cacheKey = `pros_info-${applicationNum}`;
      const idToken = localStorage.getItem('id_token');

      if (!idToken) {
        throw new Error('No ID token found');
      }

      // Check cache first if not overriding
      if (!overrideCache) {
        const cachedData = localStorage.getItem(cacheKey);
        if (cachedData && cachedData !== 'undefined') {
          try {
            return JSON.parse(cachedData);
          } catch (e) {
            console.error('Failed to parse cached prosecution doc info:', e);
          }
        }
      }

      // Fetch from API
      const encryptedData = encryptData({
        id_token: idToken,
        application_num: applicationNum
      });

      const response = await axios.post(
        `${API_CONFIG.baseUrl}/doc/pros_info`,
        { data: encryptedData }
      );

      const decryptedData = decryptData<any>(response.data);

      // Cache the result
      localStorage.setItem(cacheKey, JSON.stringify(decryptedData));
      return decryptedData;
    } catch (error) {
      console.error('Failed to fetch prosecution doc info:', error);
      throw error;
    }
  },

  // Fetch specific prosecution document
  fetchProsecutionDoc: async (applicationNum: string, docId: string, doc: any, overrideCache: boolean = false): Promise<any> => {
    try {
      const cacheKey = `pros_info-${applicationNum}-doc-${docId}`;
      const idToken = localStorage.getItem('id_token');

      if (!idToken) {
        throw new Error('No ID token found');
      }

      // Check cache first if not overriding
      if (!overrideCache) {
        const cachedData = localStorage.getItem(cacheKey);
        if (cachedData && cachedData !== 'undefined') {
          try {
            return JSON.parse(cachedData);
          } catch (e) {
            console.error('Failed to parse cached prosecution doc:', e);
          }
        }
      }

      // Fetch from API
      const encryptedData = encryptData({
        id_token: idToken,
        data: {
          application_num: applicationNum,
          document: doc,
          document_id: docId
        }
      });

      const response = await axios.post(
        `${API_CONFIG.baseUrl}/doc/pros_doc`,
        { data: encryptedData }
      );

      const decryptedData = decryptData<any>(response.data);

      // Cache the result
      localStorage.setItem(cacheKey, JSON.stringify(decryptedData));
      return decryptedData;
    } catch (error) {
      console.error('Failed to fetch prosecution doc:', error);
      throw error;
    }
  },

  // Get prosecution document URL
  getProsecutionDocUrl: async (applicationNum: string, docId: string, overrideCache: boolean = false): Promise<any> => {
    try {
      const cacheKey = `pros_info-${applicationNum}-doc-${docId}-url`;
      const idToken = localStorage.getItem('id_token');

      if (!idToken) {
        throw new Error('No ID token found');
      }

      // Check cache first if not overriding
      if (!overrideCache) {
        const cachedData = localStorage.getItem(cacheKey);
        if (cachedData && cachedData !== 'undefined') {
          try {
            return JSON.parse(cachedData);
          } catch (e) {
            console.error('Failed to parse cached prosecution doc URL:', e);
          }
        }
      }

      // Fetch from API
      const encryptedData = encryptData({
        id_token: idToken,
        data: {
          application_num: applicationNum,
          document_id: docId
        }
      });

      const response = await axios.post(
        `${API_CONFIG.baseUrl}/doc/pros_doc_url`,
        { data: encryptedData }
      );

      const decryptedData = decryptData<any>(response.data);

      // Cache the result
      localStorage.setItem(cacheKey, JSON.stringify(decryptedData));
      return decryptedData;
    } catch (error) {
      console.error('Failed to get prosecution doc URL:', error);
      throw error;
    }
  },

  // Get Office Action PDF (with job_id for polling)
  getOfficeActionPdf: async (applicationNum: string, docId: string): Promise<any> => {
    try {
      const idToken = localStorage.getItem('id_token');
      if (!idToken) {
        throw new Error('No ID token found');
      }

      const encryptedData = encryptData({
        id_token: idToken,
        app_no: applicationNum,
        doc_id: docId
      });

      const response = await axios.post(
        `${API_CONFIG.baseUrl}/doc/pdf/oa`,
        { data: encryptedData }
      );

      return decryptData<any>(response.data);
    } catch (error) {
      console.error('Failed to get office action PDF:', error);
      throw error;
    }
  },

  // Get Claims/Prosecution Document PDF (with job_id for polling)
  getProsecutionDocPdf: async (applicationNum: string, docId: string): Promise<any> => {
    try {
      const idToken = localStorage.getItem('id_token');
      if (!idToken) {
        throw new Error('No ID token found');
      }

      const encryptedData = encryptData({
        id_token: idToken,
        app_no: applicationNum,
        doc_id: docId
      });

      const response = await axios.post(
        `${API_CONFIG.baseUrl}/doc/pdf/claims`,
        { data: encryptedData }
      );

      return decryptData<any>(response.data);
    } catch (error) {
      console.error('Failed to get prosecution document PDF:', error);
      throw error;
    }
  },

  // Poll for PDF result using job_status endpoint (generic for both OA and Claims)
  getPdfJobStatus: async (jobId: string): Promise<any> => {
    try {
      const idToken = localStorage.getItem('id_token');
      if (!idToken) {
        throw new Error('No ID token found');
      }

      const encryptedData = encryptData({
        id_token: idToken,
        query: jobId
      });

      const response = await axios.post(
        `${API_CONFIG.baseUrl}/task/job_status`,
        { data: encryptedData }
      );

      return decryptData<any>(response.data);
    } catch (error) {
      console.error('Failed to get PDF job status:', error);
      return null;  // Return null to continue polling
    }
  },

  // Download Office Action PDF (after job is complete)
  downloadOfficeActionPdf: async (applicationNum: string, docId: string): Promise<string> => {
    try {
      const idToken = localStorage.getItem('id_token');
      if (!idToken) {
        throw new Error('No ID token found');
      }

      const encryptedData = encryptData({
        id_token: idToken,
        app_no: applicationNum,
        doc_id: docId
      });

      const response = await axios.post(
        `${API_CONFIG.baseUrl}/doc/pdf/oa/download`,
        { data: encryptedData },
        { responseType: 'blob' }
      );

      // Convert blob to URL
      const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
      return URL.createObjectURL(pdfBlob);
    } catch (error) {
      console.error('Failed to download office action PDF:', error);
      throw error;
    }
  },

  // Download Claims/Prosecution Document PDF (after job is complete)
  downloadProsecutionDocPdf: async (applicationNum: string, docId: string): Promise<string> => {
    try {
      const idToken = localStorage.getItem('id_token');
      if (!idToken) {
        throw new Error('No ID token found');
      }

      const encryptedData = encryptData({
        id_token: idToken,
        app_no: applicationNum,
        doc_id: docId
      });

      const response = await axios.post(
        `${API_CONFIG.baseUrl}/doc/pdf/claims/download`,
        { data: encryptedData },
        { responseType: 'blob' }
      );

      // Convert blob to URL
      const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
      return URL.createObjectURL(pdfBlob);
    } catch (error) {
      console.error('Failed to download prosecution document PDF:', error);
      throw error;
    }
  },

  // Extract Office Action Info
  extractOAInfo: async (prosDocId: string, appId: string): Promise<any> => {
    try {
      const idToken = localStorage.getItem('id_token');
      if (!idToken) {
        throw new Error('No ID token found');
      }

      const encryptedData = encryptData({
        id_token: idToken,
        pros_doc_id: prosDocId,
        app_id: appId
      });

      const response = await axios.post(
        `${API_CONFIG.baseUrl}/task/oa_extract`,
        { data: encryptedData }
      );

      return decryptData<any>(response.data);
    } catch (error) {
      console.error('Failed to extract OA info:', error);
      throw error;
    }
  },

  // Analyze Office Action Issues
  analyzeOAIssues: async (prosDocId: string, appId: string): Promise<any> => {
    try {
      const idToken = localStorage.getItem('id_token');
      if (!idToken) {
        throw new Error('No ID token found');
      }

      const encryptedData = encryptData({
        id_token: idToken,
        pros_doc_id: prosDocId,
        app_id: appId
      });

      const response = await axios.post(
        `${API_CONFIG.baseUrl}/task/oa_analyze`,
        { data: encryptedData }
      );

      return decryptData<any>(response.data);
    } catch (error) {
      console.error('Failed to analyze OA issues:', error);
      throw error;
    }
  },

  // Summarize Office Action Prior Art
  summarizeOAPriorArt: async (prosDocId: string, appId: string): Promise<any> => {
    try {
      const idToken = localStorage.getItem('id_token');
      if (!idToken) {
        throw new Error('No ID token found');
      }

      const encryptedData = encryptData({
        id_token: idToken,
        pros_doc_id: prosDocId,
        app_id: appId
      });

      const response = await axios.post(
        `${API_CONFIG.baseUrl}/task/oa_summary_art`,
        { data: encryptedData }
      );

      return decryptData<any>(response.data);
    } catch (error) {
      console.error('Failed to summarize OA prior art:', error);
      throw error;
    }
  },

  // Build Office Action Timeline
  buildOATimeline: async (applicationNo: string): Promise<any> => {
    try {
      const idToken = localStorage.getItem('id_token');
      if (!idToken) {
        throw new Error('No ID token found');
      }

      const encryptedData = encryptData({
        id_token: idToken,
        application_no: applicationNo
      });

      const response = await axios.post(
        `${API_CONFIG.baseUrl}/task/oa_timeline`,
        { data: encryptedData }
      );

      return decryptData<any>(response.data);
    } catch (error) {
      console.error('Failed to build OA timeline:', error);
      throw error;
    }
  },

  // Get Custom Analysis Result (for polling)
  getCustomAnalysisResult: async (jobId: string): Promise<any> => {
    try {
      const idToken = localStorage.getItem('id_token');
      if (!idToken) {
        throw new Error('No ID token found');
      }

      const encryptedData = encryptData({
        id_token: idToken,
        job_id: jobId
      });

      const response = await axios.post(
        `${API_CONFIG.baseUrl}/task/get_analysis`,
        { data: encryptedData }
      );

      return decryptData<any>(response.data);
    } catch (error) {
      console.error('Failed to get analysis result:', error);
      return null;
    }
  },

  // Fetch patent assignments
  fetchAssignments: async (applicationNumber: string): Promise<any> => {
    try {
      const idToken = localStorage.getItem('id_token');
      if (!idToken) {
        throw new Error('No ID token found');
      }

      const encryptedData = encryptData({
        id_token: idToken,
        application_number: applicationNumber
      });

      const response = await axios.post(
        `${API_CONFIG.baseUrl}/patent/assignments`,
        { data: encryptedData }
      );

      return decryptData<any>(response.data);
    } catch (error) {
      console.error('Failed to fetch assignments:', error);
      throw error;
    }
  },
};

// Credit API functions
export const creditApi = {
  // Get credit balance
  getCreditBalance: async (): Promise<any> => {
    try {
      const idToken = localStorage.getItem('id_token');
      const encryptedData = encryptData({ id_token: idToken });
      const response = await axios.post(`${API_CONFIG.baseUrl}/credit/balance`, { data: encryptedData });
      return decryptData(response.data);
    } catch (error) {
      console.error('Error getting credit balance:', error);
      throw error;
    }
  },

  // Purchase credits
  purchaseCredits: async (packageType: string, customAmount?: number): Promise<{ sessionId: string }> => {
    try {
      const username = localStorage.getItem('username');
      const requestData: any = {
        username,
        package: packageType,
      };

      if (customAmount !== null && customAmount !== undefined) {
        requestData.customAmount = customAmount;
      }

      const response = await axios.post(
        `${API_CONFIG.baseUrl}/stripe/create-credit-checkout-session`,
        requestData
      );
      return response.data;
    } catch (error) {
      console.error('Failed to create credit purchase session:', error);
      throw error;
    }
  },

  // Create subscription checkout
  createSubscriptionCheckout: async (planType: string): Promise<{ session_id: string }> => {
    try {
      const idToken = localStorage.getItem('id_token');
      const username = localStorage.getItem('username');
      const encryptedData = encryptData({
        id_token: idToken,
        plan_type: planType,
        username: username,
      });
      const response = await axios.post(
        `${API_CONFIG.baseUrl}/stripe/create-subscription-checkout`,
        { data: encryptedData }
      );
      return decryptData(response.data);
    } catch (error) {
      console.error('Failed to create subscription checkout:', error);
      throw error;
    }
  },

  // Confirm payment after Stripe checkout
  confirmPayment: async (sessionId: string, paymentKey: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await axios.post(
        `${API_CONFIG.baseUrl}/stripe/set-payment-successful`,
        {
          session_id: sessionId,
          payment_key: paymentKey,
        }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to confirm payment:', error);
      throw error;
    }
  },
};

// File Upload API functions
export const fileApi = {
  // Upload files to the server
  uploadFiles: async (formData: FormData): Promise<any> => {
    try {
      const idToken = localStorage.getItem('id_token');
      const sessionContext = localStorage.getItem('session_context');

      formData.append('id_token', idToken || '');
      formData.append('session_context', sessionContext || '{}');

      const response = await axios.post(
        `${API_CONFIG.baseUrl}/doc/upload_files`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      // Update session context if provided
      if (response?.data?.session_context) {
        localStorage.setItem('session_context', JSON.stringify(response.data.session_context));
        window.dispatchEvent(new Event('storage-update'));
      }

      // Store resource IDs and names if provided
      if (response?.data?.resource_ids && response?.data?.resource_names) {
        localStorage.setItem('new_resource_ids', response.data.resource_ids);
        localStorage.setItem('new_resource_names', response.data.resource_names);
      }

      return response.data;
    } catch (error) {
      console.error('Error uploading file:', error);
      throw error;
    }
  },
};

// User Account API functions
export const userApi = {
  // Fetch account information (email, account_type, etc.)
  fetchAccountInfo: async (): Promise<any> => {
    try {
      const idToken = localStorage.getItem('id_token');
      const encryptedData = encryptData({ id_token: idToken });
      const response = await axios.post(
        `${API_CONFIG.baseUrl}/user/fetch_account_info`,
        { data: encryptedData }
      );
      return decryptData(response.data);
    } catch (error) {
      console.error('Failed to fetch account info:', error);
      throw error;
    }
  },

  // Fetch usage data including daily onboarding limits
  fetchUsage: async (): Promise<any> => {
    try {
      const idToken = localStorage.getItem('id_token');
      const encryptedData = encryptData({ id_token: idToken });
      const response = await axios.post(
        `${API_CONFIG.baseUrl}/user/fetch_usage`,
        { data: encryptedData }
      );
      return decryptData(response.data);
    } catch (error) {
      console.error('Failed to fetch usage:', error);
      throw error;
    }
  },

  // Fetch user settings
  fetchSettings: async (): Promise<{ user_settings: Record<string, unknown> | 'unknown' }> => {
    try {
      const idToken = localStorage.getItem('id_token');
      const encryptedData = encryptData({ id_token: idToken });
      const response = await axios.post(
        `${API_CONFIG.baseUrl}${API_CONFIG.endpoints.user.fetchSettings}`,
        { data: encryptedData }
      );
      const decryptedResponse = decryptData(response.data);
      console.log('API: Fetched settings:', decryptedResponse);
      return decryptedResponse;
    } catch (error) {
      console.error('Failed to fetch user settings:', error);
      throw error;
    }
  },

  // Update user settings
  updateSettings: async (userSettings: Record<string, unknown>): Promise<{ user_settings: Record<string, unknown> }> => {
    try {
      const idToken = localStorage.getItem('id_token');
      console.log('API: Updating settings with:', userSettings);
      const encryptedData = encryptData({
        id_token: idToken,
        user_settings: userSettings
      });
      const response = await axios.post(
        `${API_CONFIG.baseUrl}${API_CONFIG.endpoints.user.updateSettings}`,
        { data: encryptedData }
      );
      const decryptedResponse = decryptData(response.data);
      console.log('API: Update settings response:', decryptedResponse);
      return decryptedResponse;
    } catch (error) {
      console.error('Failed to update user settings:', error);
      throw error;
    }
  },

  // Save a custom prompt
  savePrompt: async (label: string, prompt: string, availableIn: string): Promise<{ message: string; prompt_id: string }> => {
    try {
      const idToken = localStorage.getItem('id_token');
      const encryptedData = encryptData({
        id_token: idToken,
        label,
        prompt,
        available_in: availableIn,
      });
      const response = await axios.post(
        `${API_CONFIG.baseUrl}${API_CONFIG.endpoints.user.savePrompt}`,
        { data: encryptedData }
      );
      return decryptData(response.data);
    } catch (error) {
      console.error('Failed to save prompt:', error);
      throw error;
    }
  },

  // Update a custom prompt
  updatePrompt: async (promptId: string, label: string, prompt: string, availableIn: string): Promise<{ message: string }> => {
    try {
      const idToken = localStorage.getItem('id_token');
      const encryptedData = encryptData({
        id_token: idToken,
        prompt_id: promptId,
        label,
        prompt,
        available_in: availableIn,
      });
      const response = await axios.post(
        `${API_CONFIG.baseUrl}${API_CONFIG.endpoints.user.updatePrompt}`,
        { data: encryptedData }
      );
      return decryptData(response.data);
    } catch (error) {
      console.error('Failed to update prompt:', error);
      throw error;
    }
  },

  // Fetch user's custom prompts
  fetchPrompts: async (): Promise<{ prompts: Array<{ prompt_id: string; label: string; prompt: string; created_at: string; available_in?: string }> }> => {
    try {
      const idToken = localStorage.getItem('id_token');
      const encryptedData = encryptData({ id_token: idToken });
      const response = await axios.post(
        `${API_CONFIG.baseUrl}${API_CONFIG.endpoints.user.fetchPrompts}`,
        { data: encryptedData }
      );
      return decryptData(response.data);
    } catch (error) {
      console.error('Failed to fetch prompts:', error);
      throw error;
    }
  },

  // Delete a custom prompt
  deletePrompt: async (promptId: string): Promise<{ message: string }> => {
    try {
      const idToken = localStorage.getItem('id_token');
      const encryptedData = encryptData({
        id_token: idToken,
        prompt_id: promptId,
      });
      const response = await axios.post(
        `${API_CONFIG.baseUrl}${API_CONFIG.endpoints.user.deletePrompt}`,
        { data: encryptedData }
      );
      return decryptData(response.data);
    } catch (error) {
      console.error('Failed to delete prompt:', error);
      throw error;
    }
  },

  // Save a custom email template
  saveEmailTemplate: async (
    name: string,
    description: string,
    templateStructure: {
      greeting: string;
      intro: string;
      sections: string[];
      closing: string;
      signature: string;
      tone?: string;
    },
    templateId?: string
  ): Promise<{ message: string; template_id: string }> => {
    try {
      const idToken = localStorage.getItem('id_token');
      const encryptedData = encryptData({
        id_token: idToken,
        name,
        description,
        template_structure: templateStructure,
        template_id: templateId,
      });
      const response = await axios.post(
        `${API_CONFIG.baseUrl}${API_CONFIG.endpoints.user.saveEmailTemplate}`,
        { data: encryptedData }
      );
      return decryptData(response.data);
    } catch (error) {
      console.error('Failed to save email template:', error);
      throw error;
    }
  },

  // Fetch all custom email templates
  fetchEmailTemplates: async (): Promise<{ templates: Array<any> }> => {
    try {
      const idToken = localStorage.getItem('id_token');
      const encryptedData = encryptData({ id_token: idToken });
      const response = await axios.post(
        `${API_CONFIG.baseUrl}${API_CONFIG.endpoints.user.fetchEmailTemplates}`,
        { data: encryptedData }
      );
      return decryptData(response.data);
    } catch (error) {
      console.error('Failed to fetch email templates:', error);
      throw error;
    }
  },

  // Delete a custom email template
  deleteEmailTemplate: async (templateId: string): Promise<{ message: string }> => {
    try {
      const idToken = localStorage.getItem('id_token');
      const encryptedData = encryptData({
        id_token: idToken,
        template_id: templateId,
      });
      const response = await axios.post(
        `${API_CONFIG.baseUrl}${API_CONFIG.endpoints.user.deleteEmailTemplate}`,
        { data: encryptedData }
      );
      return decryptData(response.data);
    } catch (error) {
      console.error('Failed to delete email template:', error);
      throw error;
    }
  },

  // Auto-generate email template from raw text
  autoGenerateEmailTemplate: async (rawEmailText: string): Promise<any> => {
    try {
      const idToken = localStorage.getItem('id_token');
      const encryptedData = encryptData({
        id_token: idToken,
        raw_email_text: rawEmailText,
      });
      const response = await axios.post(
        `${API_CONFIG.baseUrl}${API_CONFIG.endpoints.user.autoGenerateEmailTemplate}`,
        { data: encryptedData }
      );
      return decryptData(response.data);
    } catch (error) {
      console.error('Failed to auto-generate email template:', error);
      throw error;
    }
  },
};

// Draft API functions
export const draftApi = {
  // Create a new draft
  createDraft: async (title: string, content: string = '', draftType: string = 'general', metadata: any = {}) => {
    try {
      const idToken = localStorage.getItem('id_token');
      const encryptedData = encryptData({
        id_token: idToken,
        title,
        content,
        draft_type: draftType,
        metadata
      });
      const response = await axios.post(
        `${API_CONFIG.baseUrl}/drafts/create`,
        { data: encryptedData }
      );
      return decryptData(response.data);
    } catch (error) {
      console.error('Failed to create draft:', error);
      throw error;
    }
  },

  // Get all drafts for the user
  listDrafts: async () => {
    try {
      const idToken = localStorage.getItem('id_token');
      const encryptedData = encryptData({
        id_token: idToken
      });
      const response = await axios.post(
        `${API_CONFIG.baseUrl}/drafts/list`,
        { data: encryptedData }
      );
      return decryptData(response.data);
    } catch (error) {
      console.error('Failed to list drafts:', error);
      throw error;
    }
  },

  // Get a specific draft
  getDraft: async (draftId: string) => {
    try {
      const idToken = localStorage.getItem('id_token');
      const encryptedData = encryptData({
        id_token: idToken,
        draft_id: draftId
      });
      const response = await axios.post(
        `${API_CONFIG.baseUrl}/drafts/get`,
        { data: encryptedData }
      );
      return decryptData(response.data);
    } catch (error) {
      console.error('Failed to get draft:', error);
      throw error;
    }
  },

  // Update a draft
  updateDraft: async (draftId: string, title?: string, content?: string, metadata?: any) => {
    try {
      const idToken = localStorage.getItem('id_token');
      const updateData: any = {
        id_token: idToken,
        draft_id: draftId
      };
      if (title !== undefined) updateData.title = title;
      if (content !== undefined) updateData.content = content;
      if (metadata !== undefined) updateData.metadata = metadata;

      const encryptedData = encryptData(updateData);
      const response = await axios.post(
        `${API_CONFIG.baseUrl}/drafts/update`,
        { data: encryptedData }
      );
      return decryptData(response.data);
    } catch (error) {
      console.error('Failed to update draft:', error);
      throw error;
    }
  },

  // Delete a draft
  deleteDraft: async (draftId: string) => {
    try {
      const idToken = localStorage.getItem('id_token');
      const encryptedData = encryptData({
        id_token: idToken,
        draft_id: draftId
      });
      const response = await axios.post(
        `${API_CONFIG.baseUrl}/drafts/delete`,
        { data: encryptedData }
      );
      return decryptData(response.data);
    } catch (error) {
      console.error('Failed to delete draft:', error);
      throw error;
    }
  },
};

// Task API functions
export const taskApi = {
  // Submit a parts list task
  submitPartsListTask: async (resourceId: string, resourceType: string, isApplication: boolean) => {
    try {
      const idToken = localStorage.getItem('id_token');
      const encryptedData = encryptData({
        id_token: idToken,
        doc_no: resourceId,
        doc_type: resourceType,
        is_application: isApplication,
      });
      const response = await axios.post(`${API_CONFIG.baseUrl}/task/parts_list`, { data: encryptedData });
      return decryptData(response.data);
    } catch (error) {
      console.error('Failed to submit parts list task:', error);
      throw error;
    }
  },

  // Submit a spec map task
  submitSpecMapTask: async (resourceId: string, resourceType: string, isApplication: boolean) => {
    try {
      const idToken = localStorage.getItem('id_token');
      const encryptedData = encryptData({
        id_token: idToken,
        doc_no: resourceId,
        doc_type: resourceType,
        is_application: isApplication,
      });
      const response = await axios.post(`${API_CONFIG.baseUrl}/task/spec_map`, { data: encryptedData });
      return decryptData(response.data);
    } catch (error) {
      console.error('Failed to submit spec map task:', error);
      throw error;
    }
  },

  // Submit a claims support task
  submitClaimsSupportTask: async (
    resourceId: string,
    resourceType: string,
    isApplication: boolean,
    useEntireDescription: boolean,
    useEntireClaims: boolean
  ) => {
    try {
      const idToken = localStorage.getItem('id_token');
      const encryptedData = encryptData({
        id_token: idToken,
        doc_no: resourceId,
        doc_type: resourceType,
        is_application: isApplication,
        use_entire_description: useEntireDescription,
        use_entire_claims: useEntireClaims,
      });
      const response = await axios.post(`${API_CONFIG.baseUrl}/task/claims_support`, { data: encryptedData });
      return decryptData(response.data);
    } catch (error) {
      console.error('Failed to submit claims support task:', error);
      throw error;
    }
  },

  // Submit a claims with numerals task
  submitClaimsWithNumeralsTask: async (resourceId: string, resourceType: string, isApplication: boolean) => {
    try {
      const idToken = localStorage.getItem('id_token');
      const encryptedData = encryptData({
        id_token: idToken,
        doc_no: resourceId,
        doc_type: resourceType,
        is_application: isApplication,
      });
      const response = await axios.post(`${API_CONFIG.baseUrl}/task/claims_with_numerals`, { data: encryptedData });
      return decryptData(response.data);
    } catch (error) {
      console.error('Failed to submit claims with numerals task:', error);
      throw error;
    }
  },

  // Submit a custom analysis task
  submitCustomAnalysisTask: async (resourceId: string, resourceType: string, isApplication: boolean, instruction: string) => {
    try {
      const idToken = localStorage.getItem('id_token');
      const encryptedData = encryptData({
        id_token: idToken,
        user_prompt: instruction,
        selected_number: resourceId,
        resource_type: resourceType,
      });
      const response = await axios.post(`${API_CONFIG.baseUrl}/task/custom_analysis`, { data: encryptedData });
      return decryptData(response.data);
    } catch (error) {
      console.error('Failed to submit custom analysis task:', error);
      throw error;
    }
  },

  // Submit IR metadata task
  submitIRMetadataTask: async (textContent: string) => {
    try {
      const idToken = localStorage.getItem('id_token');
      const encryptedData = encryptData({
        id_token: idToken,
        text_content: textContent,
      });
      const response = await axios.post(`${API_CONFIG.baseUrl}/task/ir_metadata`, { data: encryptedData });
      return decryptData(response.data);
    } catch (error) {
      console.error('Failed to submit IR metadata task:', error);
      throw error;
    }
  },

  // Submit IR innovation analysis task
  submitIRInnovationTask: async (textContent: string) => {
    try {
      const idToken = localStorage.getItem('id_token');
      const encryptedData = encryptData({
        id_token: idToken,
        text_content: textContent,
      });
      const response = await axios.post(`${API_CONFIG.baseUrl}/task/ir_innovation`, { data: encryptedData });
      return decryptData(response.data);
    } catch (error) {
      console.error('Failed to submit IR innovation task:', error);
      throw error;
    }
  },

  // Submit IR tech classification task
  submitIRTechClassTask: async (textContent: string) => {
    try {
      const idToken = localStorage.getItem('id_token');
      const encryptedData = encryptData({
        id_token: idToken,
        text_content: textContent,
      });
      const response = await axios.post(`${API_CONFIG.baseUrl}/task/ir_tech_class`, { data: encryptedData });
      return decryptData(response.data);
    } catch (error) {
      console.error('Failed to submit IR tech classification task:', error);
      throw error;
    }
  },

  // Submit draft summary task
  submitDraftSummaryTask: async (textContent: string, selectedDocument?: string) => {
    try {
      const idToken = localStorage.getItem('id_token');
      const payload: any = {
        id_token: idToken,
        text_content: textContent,
      };
      if (selectedDocument) {
        payload.selected_document = selectedDocument;
      }
      const encryptedData = encryptData(payload);
      const response = await axios.post(`${API_CONFIG.baseUrl}/task/draft_summary`, { data: encryptedData });
      return decryptData(response.data);
    } catch (error) {
      console.error('Failed to submit draft summary task:', error);
      throw error;
    }
  },

  // Submit draft detailed description task
  submitDraftDetailedDescriptionTask: async (userTextInput: string, inventionDisclosureText?: string, context?: string) => {
    try {
      const idToken = localStorage.getItem('id_token');
      const encryptedData = encryptData({
        id_token: idToken,
        user_text_input: userTextInput,
        invention_disclosure_text: inventionDisclosureText || '',
        context: context || '',
      });
      const response = await axios.post(`${API_CONFIG.baseUrl}/task/draft_detailed_description`, { data: encryptedData });
      return decryptData(response.data);
    } catch (error) {
      console.error('Failed to submit draft detailed description task:', error);
      throw error;
    }
  },

  // Submit draft background task
  submitDraftBackgroundTask: async (textContent: string, selectedDocument?: string) => {
    try {
      const idToken = localStorage.getItem('id_token');
      const payload: any = {
        id_token: idToken,
        text_content: textContent,
      };
      if (selectedDocument) {
        payload.selected_document = selectedDocument;
      }
      const encryptedData = encryptData(payload);
      const response = await axios.post(`${API_CONFIG.baseUrl}/task/draft_background`, { data: encryptedData });
      return decryptData(response.data);
    } catch (error) {
      console.error('Failed to submit draft background task:', error);
      throw error;
    }
  },

  // Submit draft claims task
  submitDraftClaimsTask: async (textContent: string, selectedDocument?: string) => {
    try {
      const idToken = localStorage.getItem('id_token');
      const payload: any = {
        id_token: idToken,
        text_content: textContent,
      };
      if (selectedDocument) {
        payload.selected_document = selectedDocument;
      }
      const encryptedData = encryptData(payload);
      const response = await axios.post(`${API_CONFIG.baseUrl}/task/draft_claims`, { data: encryptedData });
      return decryptData(response.data);
    } catch (error) {
      console.error('Failed to submit draft claims task:', error);
      throw error;
    }
  },

  // Submit draft other embodiments task
  submitDraftOtherEmbodimentsTask: async (userTextInput: string, selectedDocument?: string) => {
    try {
      const idToken = localStorage.getItem('id_token');
      const payload: any = {
        id_token: idToken,
        user_text_input: userTextInput,
      };
      if (selectedDocument) {
        payload.selected_document = selectedDocument;
      }
      const encryptedData = encryptData(payload);
      const response = await axios.post(`${API_CONFIG.baseUrl}/task/draft_other_embodiments`, { data: encryptedData });
      return decryptData(response.data);
    } catch (error) {
      console.error('Failed to submit draft other embodiments task:', error);
      throw error;
    }
  },

  // Submit OA extract task
  submitOAExtractTask: async (resourceId: string, resourceType: string) => {
    try {
      const idToken = localStorage.getItem('id_token');
      const encryptedData = encryptData({
        id_token: idToken,
        pros_doc_id: resourceId,
        app_id: resourceType,
      });
      const response = await axios.post(`${API_CONFIG.baseUrl}/task/oa_extract`, { data: encryptedData });
      return decryptData(response.data);
    } catch (error) {
      console.error('Failed to submit OA extract task:', error);
      throw error;
    }
  },

  // Submit OA analyze task
  submitOAAnalyzeTask: async (resourceId: string, resourceType: string) => {
    try {
      const idToken = localStorage.getItem('id_token');
      const encryptedData = encryptData({
        id_token: idToken,
        pros_doc_id: resourceId,
        app_id: resourceType,
      });
      const response = await axios.post(`${API_CONFIG.baseUrl}/task/oa_analyze`, { data: encryptedData });
      return decryptData(response.data);
    } catch (error) {
      console.error('Failed to submit OA analyze task:', error);
      throw error;
    }
  },

  // Submit OA summary art task
  submitOASummaryArtTask: async (resourceId: string, resourceType: string) => {
    try {
      const idToken = localStorage.getItem('id_token');
      const encryptedData = encryptData({
        id_token: idToken,
        pros_doc_id: resourceId,
        app_id: resourceType,
      });
      const response = await axios.post(`${API_CONFIG.baseUrl}/task/oa_summary_art`, { data: encryptedData });
      return decryptData(response.data);
    } catch (error) {
      console.error('Failed to submit OA summary art task:', error);
      throw error;
    }
  },

  // Submit OA reporting email task
  submitOAReportingEmailTask: async (resourceId: string, resourceType: string, emailTemplate: string = 'standard') => {
    try {
      const idToken = localStorage.getItem('id_token');
      const encryptedData = encryptData({
        id_token: idToken,
        pros_doc_id: resourceId,
        app_id: resourceType,
        email_template: emailTemplate,
      });
      const response = await axios.post(`${API_CONFIG.baseUrl}/task/oa_reporting_email`, { data: encryptedData });
      return decryptData(response.data);
    } catch (error) {
      console.error('Failed to submit OA reporting email task:', error);
      throw error;
    }
  },

  // Submit OA timeline task
  submitOATimelineTask: async (applicationNo: string) => {
    try {
      const idToken = localStorage.getItem('id_token');
      const encryptedData = encryptData({
        id_token: idToken,
        application_no: applicationNo,
      });
      const response = await axios.post(`${API_CONFIG.baseUrl}/task/oa_timeline`, { data: encryptedData });
      return decryptData(response.data);
    } catch (error) {
      console.error('Failed to submit OA timeline task:', error);
      throw error;
    }
  },

  // Submit translate document task
  submitTranslateDocumentTask: async (params: {
    text_content?: string;
    resource_id?: string;
    resource_type?: string;
    source_language: string;
    target_language: string;
  }) => {
    try {
      const idToken = localStorage.getItem('id_token');
      const payload: any = {
        id_token: idToken,
        source_language: params.source_language || 'auto',
        target_language: params.target_language || 'en',
      };

      if (params.resource_id && params.resource_type) {
        payload.resource_id = params.resource_id;
        payload.resource_type = params.resource_type;
      } else if (params.text_content) {
        payload.text_content = params.text_content;
      } else {
        throw new Error('Either resource_id/resource_type or text_content must be provided');
      }

      const encryptedData = encryptData(payload);
      const response = await axios.post(`${API_CONFIG.baseUrl}/task/translate_document`, { data: encryptedData });
      return decryptData(response.data);
    } catch (error) {
      console.error('Failed to submit translate document task:', error);
      throw error;
    }
  },

  // Get job result by polling
  getJobResult: async (jobId: string) => {
    try {
      const idToken = localStorage.getItem('id_token');
      const encryptedData = encryptData({
        job_id: jobId,
        id_token: idToken,
      });
      const response = await axios.post(
        `${API_CONFIG.baseUrl}/task/get_analysis`,
        { data: encryptedData },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      return decryptData(response.data);
    } catch (error) {
      console.error('Failed to get job result:', error);
      return null;  // Return null to continue polling instead of throwing
    }
  },

  // Get task history
  getTaskHistory: async (taskTypeFilter?: string) => {
    try {
      const idToken = localStorage.getItem('id_token');
      const requestData: any = { id_token: idToken };
      if (taskTypeFilter) {
        requestData.task_type_filter = taskTypeFilter;
      }
      const encryptedData = encryptData(requestData);
      const response = await axios.post(
        `${API_CONFIG.baseUrl}/task/get_analysis_history`,
        { data: encryptedData }
      );
      return decryptData(response.data);
    } catch (error) {
      console.error('Failed to get task history:', error);
      throw error;
    }
  },

  // Graduated polling function
  startGraduatedPolling: (
    pollFunction: () => Promise<any>,
    onSuccess: (result: any) => void,
    onError: (error: Error) => void,
    onProgress?: (attempts: number, elapsedTime: number) => void
  ) => {
    let attempts = 0;
    let timeoutId: NodeJS.Timeout | null = null;
    let isStopped = false;

    const getNextInterval = (attemptCount: number) => {
      if (attemptCount <= 10) return 3000; // 3 seconds for first 30 seconds
      if (attemptCount <= 22) return 5000; // 5 seconds for next 60 seconds
      return 10000; // 10 seconds for remaining time
    };

    const poll = async () => {
      if (isStopped) return;

      attempts++;
      const maxDurationMs = 10 * 60 * 1000; // 10 minutes
      const elapsedTime =
        attempts <= 10
          ? attempts * 3000
          : attempts <= 22
          ? 30000 + (attempts - 10) * 5000
          : 30000 + 60000 + (attempts - 22) * 10000;

      // Check maximum duration
      if (elapsedTime >= maxDurationMs) {
        onError(new Error(`Polling timeout after ${maxDurationMs / 1000} seconds (${attempts} attempts)`));
        return;
      }

      if (onProgress) {
        onProgress(attempts, elapsedTime);
      }

      try {
        const result = await pollFunction();

        // If result is truthy, we're done
        if (result) {
          onSuccess(result);
          return;
        }

        // Schedule next poll
        const nextInterval = getNextInterval(attempts);
        timeoutId = setTimeout(poll, nextInterval);
      } catch (error) {
        onError(error as Error);
      }
    };

    // Start polling
    poll();

    // Return a stop function
    return () => {
      isStopped = true;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  },

  // Submit LaTeX to DOCX conversion task
  submitLatexConversionTask: async (file: File, outputFilename?: string) => {
    try {
      const idToken = localStorage.getItem('id_token');
      const formData = new FormData();
      formData.append('idToken', idToken || '');
      formData.append('file', file);
      if (outputFilename) {
        formData.append('output_filename', outputFilename);
      }

      const response = await axios.post(`${API_CONFIG.baseUrl}/task/convert/latex-to-word`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return response.data;
    } catch (error) {
      console.error('Failed to submit LaTeX conversion task:', error);
      throw error;
    }
  },

  // Check LaTeX conversion status
  getLatexConversionStatus: async (jobId: string) => {
    try {
      const idToken = localStorage.getItem('id_token');
      const response = await axios.get(
        `${API_CONFIG.baseUrl}/task/convert/latex-to-word/status/${jobId}`,
        {
          params: { idToken },
        }
      );

      return response.data;
    } catch (error) {
      console.error('Failed to get LaTeX conversion status:', error);
      throw error;
    }
  },

  // Submit PDF OCR task
  submitOcrPdfTask: async (file: File, outputFilename?: string, outputFormat?: 'txt' | 'json' | 'docx') => {
    try {
      const idToken = localStorage.getItem('id_token');
      const formData = new FormData();
      formData.append('idToken', idToken || '');
      formData.append('file', file);
      if (outputFilename) {
        formData.append('output_filename', outputFilename);
      }
      if (outputFormat) {
        formData.append('output_format', outputFormat);
      }

      const response = await axios.post(`${API_CONFIG.baseUrl}/task/convert/ocr-pdf`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return response.data;
    } catch (error) {
      console.error('Failed to submit PDF OCR task:', error);
      throw error;
    }
  },

  // Check PDF OCR status
  getOcrPdfStatus: async (jobId: string) => {
    try {
      const idToken = localStorage.getItem('id_token');
      const response = await axios.get(
        `${API_CONFIG.baseUrl}/task/convert/ocr-pdf/status/${jobId}`,
        {
          params: { idToken },
        }
      );

      return response.data;
    } catch (error) {
      console.error('Failed to get PDF OCR status:', error);
      throw error;
    }
  },

  // Submit document file translation task
  submitTranslateDocumentFileTask: async (
    file: File,
    sourceLanguage: string,
    targetLanguage: string,
    outputFilename?: string,
    outputFormat?: 'txt' | 'docx' | 'json'
  ) => {
    try {
      const idToken = localStorage.getItem('id_token');
      const formData = new FormData();
      formData.append('idToken', idToken || '');
      formData.append('file', file);
      formData.append('source_language', sourceLanguage);
      formData.append('target_language', targetLanguage);
      if (outputFilename) {
        formData.append('output_filename', outputFilename);
      }
      if (outputFormat) {
        formData.append('output_format', outputFormat);
      }

      const response = await axios.post(`${API_CONFIG.baseUrl}/task/translate_document_file`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return response.data;
    } catch (error) {
      console.error('Failed to submit document file translation task:', error);
      throw error;
    }
  },

  // Check document file translation status
  getTranslateDocumentFileStatus: async (jobId: string) => {
    try {
      const idToken = localStorage.getItem('id_token');
      const response = await axios.get(
        `${API_CONFIG.baseUrl}/task/translate_document_file/status/${jobId}`,
        {
          headers: {
            Authorization: `Bearer ${idToken}`,
          },
        }
      );

      return response.data;
    } catch (error) {
      console.error('Failed to get document file translation status:', error);
      throw error;
    }
  },

  // Submit antecedent basis check task
  submitAntecedentBasisCheck: async (
    inputType: 'raw_text' | 'patent_number' | 'upload',
    inputData: string | File,
    customInstruction?: string,
    outputFormat?: 'json' | 'txt'
  ) => {
    try {
      const idToken = localStorage.getItem('id_token');

      // Handle file upload differently
      if (inputType === 'upload' && inputData instanceof File) {
        const formData = new FormData();
        formData.append('idToken', idToken || '');
        formData.append('input_type', inputType);
        formData.append('file', inputData);
        if (customInstruction) {
          formData.append('custom_instruction', customInstruction);
        }
        if (outputFormat) {
          formData.append('output_format', outputFormat);
        }

        const response = await axios.post(`${API_CONFIG.baseUrl}/task/antecedent_basis_check`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        return response.data;
      } else {
        // Handle raw_text and patent_number
        const response = await axios.post(`${API_CONFIG.baseUrl}/task/antecedent_basis_check`, {
          idToken,
          input_type: inputType,
          input_data: inputData,
          custom_instruction: customInstruction || '',
          output_format: outputFormat || 'json',
        });

        return response.data;
      }
    } catch (error) {
      console.error('Failed to submit antecedent basis check task:', error);
      throw error;
    }
  },

  // Check antecedent basis check status
  getAntecedentBasisCheckStatus: async (jobId: string) => {
    try {
      const idToken = localStorage.getItem('id_token');
      const response = await axios.get(
        `${API_CONFIG.baseUrl}/task/antecedent_basis_check/status/${jobId}`,
        {
          headers: {
            Authorization: `Bearer ${idToken}`,
          },
        }
      );

      return response.data;
    } catch (error) {
      console.error('Failed to get antecedent basis check status:', error);
      throw error;
    }
  },

  // Submit technical outline generation task
  submitTechnicalOutline: async (
    inputType: 'raw_text' | 'upload',
    inputData: string | File,
    customInstruction?: string,
    outputFormat?: 'json' | 'txt'
  ) => {
    try {
      const idToken = localStorage.getItem('id_token');

      // Handle file upload differently
      if (inputType === 'upload' && inputData instanceof File) {
        const formData = new FormData();
        formData.append('idToken', idToken || '');
        formData.append('input_type', inputType);
        formData.append('file', inputData);
        if (customInstruction) {
          formData.append('custom_instruction', customInstruction);
        }
        if (outputFormat) {
          formData.append('output_format', outputFormat);
        }

        const response = await axios.post(`${API_CONFIG.baseUrl}/task/technical_outline`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        return response.data;
      } else {
        // Handle raw_text
        const response = await axios.post(`${API_CONFIG.baseUrl}/task/technical_outline`, {
          idToken,
          input_type: inputType,
          input_data: inputData,
          custom_instruction: customInstruction || '',
          output_format: outputFormat || 'json',
        });

        return response.data;
      }
    } catch (error) {
      console.error('Failed to submit technical outline task:', error);
      throw error;
    }
  },

  // Check technical outline generation status
  getTechnicalOutlineStatus: async (jobId: string) => {
    try {
      const idToken = localStorage.getItem('id_token');
      const response = await axios.get(
        `${API_CONFIG.baseUrl}/task/technical_outline/status/${jobId}`,
        {
          headers: {
            Authorization: `Bearer ${idToken}`,
          },
        }
      );

      return response.data;
    } catch (error) {
      console.error('Failed to get technical outline status:', error);
      throw error;
    }
  },

  // Submit parts and process outline generation task
  submitPartsProcessOutline: async (
    inputType: 'raw_text' | 'upload',
    inputData: string | File,
    customInstruction?: string,
    outputFormat?: 'json' | 'txt'
  ) => {
    try {
      const idToken = localStorage.getItem('id_token');

      // Handle file upload differently
      if (inputType === 'upload' && inputData instanceof File) {
        const formData = new FormData();
        formData.append('idToken', idToken || '');
        formData.append('input_type', inputType);
        formData.append('file', inputData);
        if (customInstruction) {
          formData.append('custom_instruction', customInstruction);
        }
        if (outputFormat) {
          formData.append('output_format', outputFormat);
        }

        const response = await axios.post(`${API_CONFIG.baseUrl}/task/parts_process_outline`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        return response.data;
      } else {
        // Handle raw_text
        const response = await axios.post(`${API_CONFIG.baseUrl}/task/parts_process_outline`, {
          idToken,
          input_type: inputType,
          input_data: inputData,
          custom_instruction: customInstruction || '',
          output_format: outputFormat || 'json',
        });

        return response.data;
      }
    } catch (error) {
      console.error('Failed to submit parts/process outline task:', error);
      throw error;
    }
  },

  // Check parts/process outline generation status
  getPartsProcessOutlineStatus: async (jobId: string) => {
    try {
      const idToken = localStorage.getItem('id_token');
      const response = await axios.get(
        `${API_CONFIG.baseUrl}/task/parts_process_outline/status/${jobId}`,
        {
          headers: {
            Authorization: `Bearer ${idToken}`,
          },
        }
      );

      return response.data;
    } catch (error) {
      console.error('Failed to get parts/process outline status:', error);
      throw error;
    }
  },
};
